import React, { useState, useEffect } from 'react';
import { Note } from '../../types';
import {
  X,
  Pin,
  Check,
  FileText,
  Trash2,
  Copy,
  Calendar,
} from 'lucide-react';

interface NoteEditorModalProps {
  isOpen: boolean;
  note: Note | null;
  onClose: () => void;
  onSave: (noteData: Partial<Note>) => void;
  onDelete?: (id: string) => void;
  onDuplicate?: (id: string) => void;
}

export const NoteEditorModal: React.FC<NoteEditorModalProps> = ({
  isOpen,
  note,
  onClose,
  onSave,
  onDelete,
  onDuplicate,
}) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [pinned, setPinned] = useState(false);
  const [reminder, setReminder] = useState('');

  const isEditing = Boolean(note?.id);

  useEffect(() => {
    if (note && note.id) {
      setTitle(note.title);
      setContent(note.content || '');
      setPinned(Boolean(note.pinned));
      setReminder(note.reminder || '');
    } else {
      setTitle('');
      setContent('');
      setPinned(false);
      setReminder('');
    }
  }, [note, isOpen]);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() && !content.trim()) return;

    onSave({
      ...(isEditing ? { id: note!.id } : {}),
      title: title.trim() || 'Untitled Note',
      content: content.trim(),
      folder: 'General',
      tags: [],
      pinned,
      reminder: reminder || undefined,
    });

    onClose();
  };

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center p-2.5 sm:p-4 bg-black/60 backdrop-blur-sm animate-fade-in overflow-y-auto"
    >
      <div className="w-full max-w-xl bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden animate-scale-in my-auto">
        {/* Modal Header */}
        <div className="px-4 sm:px-6 py-3.5 sm:py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2.5 sm:gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-purple-500/10 text-purple-500 flex items-center justify-center shrink-0">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white truncate">
              {isEditing ? 'Edit Note' : 'Create Note'}
            </h3>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setPinned(!pinned)}
              className={`flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${
                pinned
                  ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-amber-500'
              }`}
            >
              <Pin className={`w-3.5 h-3.5 ${pinned ? 'fill-amber-500 text-amber-500' : ''}`} />
              <span className="hidden xs:inline sm:inline">{pinned ? 'Pinned' : 'Pin to top'}</span>
            </button>

            <button
              type="button"
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-xl"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Main Form */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-3.5 sm:space-y-4">
          <div>
            <input
              type="text"
              placeholder="Note Title..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3.5 sm:px-4 py-2.5 sm:py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl sm:rounded-2xl text-sm sm:text-base font-bold text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              autoFocus
            />
          </div>

          <div>
            <textarea
              rows={6}
              placeholder="Write your note content here..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full px-3.5 sm:px-4 py-2.5 sm:py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl sm:rounded-2xl text-xs sm:text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none font-sans leading-relaxed"
            />
            <div className="flex items-center justify-between text-[11px] text-slate-400 mt-1 px-1">
              <span>Markdown supported</span>
              <span>
                {content.length} chars • {content.trim() ? content.trim().split(/\s+/).length : 0}{' '}
                words
              </span>
            </div>
          </div>

          {/* Calendar Reminder row */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-blue-500 shrink-0" />
              <span className="truncate">Add to Calendar / Reminder Date (Optional)</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={reminder}
                onChange={(e) => setReminder(e.target.value)}
                className="flex-1 min-w-0 px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-medium text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              {reminder && (
                <button
                  type="button"
                  onClick={() => setReminder('')}
                  className="px-2.5 py-2 text-xs text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-xl font-bold shrink-0"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Modal Footer Actions */}
          {isEditing && (onDelete || onDuplicate) ? (
            <div className="pt-3.5 sm:pt-4 border-t border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 sm:gap-3">
              {/* Secondary Actions for existing notes: Delete & Duplicate */}
              <div className="flex items-center gap-2 order-2 sm:order-1 w-full sm:w-auto">
                {onDelete && (
                  <button
                    type="button"
                    onClick={() => {
                      onDelete(note!.id);
                      onClose();
                    }}
                    className="flex-1 sm:flex-initial px-3 py-2 text-xs font-bold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-xl flex items-center justify-center gap-1.5 transition-colors border border-rose-200/50 dark:border-rose-900/40 sm:border-transparent whitespace-nowrap"
                    title="Move to Trash"
                  >
                    <Trash2 className="w-3.5 h-3.5 shrink-0" />
                    <span>Move to Trash</span>
                  </button>
                )}

                {onDuplicate && (
                  <button
                    type="button"
                    onClick={() => {
                      onDuplicate(note!.id);
                      onClose();
                    }}
                    className="flex-1 sm:flex-initial px-3 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl flex items-center justify-center gap-1.5 transition-colors border border-slate-200 dark:border-slate-700 sm:border-transparent whitespace-nowrap"
                    title="Duplicate Note"
                  >
                    <Copy className="w-3.5 h-3.5 shrink-0" />
                    <span>Duplicate</span>
                  </button>
                )}
              </div>

              {/* Primary Actions: Cancel & Save */}
              <div className="flex items-center gap-2 order-1 sm:order-2 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 sm:flex-initial px-4 sm:px-5 py-2.5 text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors text-center whitespace-nowrap"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="flex-1 sm:flex-initial px-5 sm:px-6 py-2.5 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white rounded-xl text-xs font-bold shadow-md shadow-purple-500/25 flex items-center justify-center gap-1.5 transition-all transform active:scale-95 whitespace-nowrap"
                >
                  <Check className="w-4 h-4 shrink-0" />
                  <span>Save Note</span>
                </button>
              </div>
            </div>
          ) : (
            /* Clean New Note Footer (Only Cancel & Save) */
            <div className="pt-3.5 sm:pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end gap-2 sm:gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 sm:flex-initial px-4 sm:px-5 py-2.5 text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors text-center whitespace-nowrap"
              >
                Cancel
              </button>

              <button
                type="submit"
                className="flex-1 sm:flex-initial px-5 sm:px-6 py-2.5 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white rounded-xl text-xs font-bold shadow-md shadow-purple-500/25 flex items-center justify-center gap-1.5 transition-all transform active:scale-95 whitespace-nowrap"
              >
                <Check className="w-4 h-4 shrink-0" />
                <span>Save Note</span>
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};
